<template>
  <div class="contract-apply-detail-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('contractApplyDetail.page.title')" :subtitle="$t('contractApplyDetail.page.subtitle')">
      <template #extra>
        <el-button icon="el-icon-back" @click="_goBack">{{ $t('contractApplyDetail.back') }}</el-button>
        <el-button type="primary" icon="el-icon-printer" @click="_printContract">{{ $t('contractApplyDetail.print') }}</el-button>
      </template>
    </ds-page-header>

    <!-- (2) 合同信息面板 -->
    <div class="ds-panel ds-panel--padded">
      <div class="ds-panel__header">
        <span class="ds-panel__title">{{ $t('contractApplyDetail.title') }}</span>
      </div>
      <div class="ds-info-grid">
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractApplyDetail.contractName') }}</span>
          <span class="ds-info-grid__value">{{ contractDetailInfo.contractName || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractApplyDetail.contractCode') }}</span>
          <span class="ds-info-grid__value ds-num">{{ contractDetailInfo.contractCode || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractApplyDetail.contractType') }}</span>
          <span class="ds-info-grid__value">{{ contractDetailInfo.contractTypeName || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractApplyDetail.partyA') }}</span>
          <span class="ds-info-grid__value">{{ contractDetailInfo.partyA || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractApplyDetail.partyAContact') }}</span>
          <span class="ds-info-grid__value">{{ contractDetailInfo.aContacts || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractApplyDetail.partyAPhone') }}</span>
          <span class="ds-info-grid__value ds-num">{{ contractDetailInfo.aLink || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractApplyDetail.partyB') }}</span>
          <span class="ds-info-grid__value">{{ contractDetailInfo.partyB || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractApplyDetail.partyBContact') }}</span>
          <span class="ds-info-grid__value">{{ contractDetailInfo.bContacts || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractApplyDetail.partyBPhone') }}</span>
          <span class="ds-info-grid__value ds-num">{{ contractDetailInfo.bLink || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractApplyDetail.operator') }}</span>
          <span class="ds-info-grid__value">{{ contractDetailInfo.operator || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractApplyDetail.phone') }}</span>
          <span class="ds-info-grid__value ds-num">{{ contractDetailInfo.operatorLink || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractApplyDetail.contractAmount') }}</span>
          <span class="ds-info-grid__value ds-num">{{ formatMoney(contractDetailInfo.amount) }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractApplyDetail.startTime') }}</span>
          <span class="ds-info-grid__value ds-num">{{ contractDetailInfo.startTime || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractApplyDetail.endTime') }}</span>
          <span class="ds-info-grid__value ds-num">{{ contractDetailInfo.endTime || '-' }}</span>
        </div>
        <div class="ds-info-grid__item">
          <span class="ds-info-grid__label">{{ $t('contractApplyDetail.signingTime') }}</span>
          <span class="ds-info-grid__value ds-num">{{ contractDetailInfo.signingTime || '-' }}</span>
        </div>
        <div class="ds-info-grid__item ds-info-grid__item--wide">
          <span class="ds-info-grid__label">{{ $t('contractApplyDetail.relatedFiles') }}</span>
          <span class="ds-info-grid__value">
            <span v-if="!contractDetailInfo.files || contractDetailInfo.files.length === 0">-</span>
            <span v-for="(file, index) in contractDetailInfo.files" :key="index" class="ds-file-link">
              <a href="javascript:void(0)" @click="_viewFile(file)">{{ file.fileRealName }}</a>
            </span>
          </span>
        </div>
      </div>
    </div>

    <!-- (3) 关联房屋 -->
    <div class="ds-panel">
      <div class="ds-panel__header ds-panel__header--padded">
        <span class="ds-panel__title">{{ $t('contractApplyDetail.relatedRooms') }}</span>
      </div>
      <ds-data-table :data="contractDetailInfo.rooms || []" border
        :empty-description="$t('contractApplyDetail.relatedRooms')">
        <el-table-column :label="$t('contractApplyDetail.room')" align="center" min-width="140">
          <template slot-scope="scope">{{ scope.row.roomName || '-' }}</template>
        </el-table-column>
        <el-table-column :label="$t('contractApplyDetail.roomCode')" align="center" width="140">
          <template slot-scope="scope"><span class="ds-num">{{ scope.row.roomId || '-' }}</span></template>
        </el-table-column>
        <el-table-column :label="$t('contractApplyDetail.builtUpArea')" align="center" width="160">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.builtUpArea || '-' }}{{ $t('contractApplyDetail.squareMeter') }}</span>
          </template>
        </el-table-column>
      </ds-data-table>
    </div>

    <!-- (4) 审批流 -->
    <div class="ds-panel">
      <div class="ds-panel__header ds-panel__header--padded">
        <span class="ds-panel__title">{{ $t('contractApplyDetail.workflowFlow') }}</span>
      </div>
      <ds-data-table :data="auditUsers" border
        :empty-description="$t('contractApplyDetail.workflowFlow')">
        <el-table-column :label="$t('contractApplyDetail.serialNumber')" width="80" align="center">
          <template slot-scope="scope"><span class="ds-num">{{ scope.$index + 1 }}</span></template>
        </el-table-column>
        <el-table-column :label="$t('contractApplyDetail.processor')" align="center" min-width="120">
          <template slot-scope="scope">{{ scope.row.userName || scope.row.auditName || '-' }}</template>
        </el-table-column>
        <el-table-column prop="stateName" :label="$t('contractApplyDetail.status')" align="center" width="120">
          <template slot-scope="scope">
            <ds-status-tag :label="scope.row.stateName || '-'" :text="scope.row.stateName" type="info" />
          </template>
        </el-table-column>
        <el-table-column prop="auditTime" :label="$t('contractApplyDetail.processTime')" align="center" width="160">
          <template slot-scope="scope"><span class="ds-num">{{ scope.row.auditTime || '-' }}</span></template>
        </el-table-column>
        <el-table-column prop="duration" :label="$t('contractApplyDetail.timeConsuming')" align="center" width="120">
          <template slot-scope="scope"><span class="ds-num">{{ scope.row.duration || '-' }}</span></template>
        </el-table-column>
        <el-table-column prop="message" :label="$t('contractApplyDetail.opinion')" align="center" min-width="160" show-overflow-tooltip />
      </ds-data-table>
    </div>
  </div>
</template>

<script>
import { queryContract, getContractFiles, getContractRooms } from '@/api/contract/contractDetailApi'
import { listWorkflowAuditInfo } from '@/api/contract/contractDetailFlowApi'
import { getCommunityId } from '@/api/community/communityApi'
import { formatMoney } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'ContractApplyDetail',
  components: { DsPageHeader, DsDataTable, DsStatusTag },
  data() {
    return {
      contractDetailInfo: {
        contractId: '', contractName: '', contractCode: '', contractType: '', contractTypeName: '',
        partyA: '', partyB: '', aContacts: '', bContacts: '', aLink: '', bLink: '',
        operator: '', operatorLink: '', amount: '', startTime: '', endTime: '', signingTime: '',
        param: '', planType: '', files: [], rooms: []
      },
      auditUsers: [],
      communityId: ''
    }
  },
  created() {
    this.communityId = getCommunityId()
    this.contractDetailInfo.contractId = this.$route.query.contractId
    if (!this.contractDetailInfo.contractId) {
      this.$message.error('缺少合同ID参数')
      this.$router.go(-1)
      return
    }
    this._initMethod()
  },
  methods: {
    formatMoney(v) { return formatMoney(v) },
    async _initMethod() {
      await Promise.all([this._listContractApply(), this._loadAuditUser(), this._loadContractFiles(), this._loadContractRooms()])
    },
    async _listContractApply() {
      try {
        const res = await queryContract({ page: 1, row: 1, contractId: this.contractDetailInfo.contractId })
        if (res.data && res.data.length > 0) Object.assign(this.contractDetailInfo, res.data[0])
      } catch (error) {
        console.error('请求失败:', error)
        this.$message.error('获取合同信息失败')
      }
    },
    async _loadAuditUser() {
      try {
        const res = await listWorkflowAuditInfo({ businessKey: this.contractDetailInfo.contractId, communityId: this.communityId })
        if (res.data) this.auditUsers = res.data
      } catch (error) { console.error('请求失败:', error) }
    },
    async _loadContractFiles() {
      try {
        const res = await getContractFiles({ contractId: this.contractDetailInfo.contractId, page: 1, row: 100 })
        if (res.data) this.contractDetailInfo.files = res.data
      } catch (error) { console.error('请求失败:', error) }
    },
    async _loadContractRooms() {
      try {
        const res = await getContractRooms({ contractId: this.contractDetailInfo.contractId, page: 1, row: 100 })
        if (res.data) this.contractDetailInfo.rooms = res.data
      } catch (error) { console.error('请求失败:', error) }
    },
    _viewFile(file) { if (file.fileSaveName) window.open(file.fileSaveName) },
    _goBack() { this.$router.go(-1) },
    _printContract() {
      const c = this.contractDetailInfo
      window.open(`/#/views/contract/printContract?contractTypeId=${c.contractType}&contractId=${c.contractId}`)
    }
  }
}
</script>

<style lang="scss" scoped>
.contract-apply-detail-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
  margin-bottom: var(--ds-spacing-lg);
  &--padded { padding: var(--ds-spacing-lg); }
}
.ds-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: var(--ds-spacing-md);
  margin-bottom: var(--ds-spacing-lg);
  border-bottom: 1px solid var(--ds-gray-200);
  &--padded { margin: 0; padding: var(--ds-spacing-md) var(--ds-spacing-lg); }
}
.ds-panel__title {
  font-size: var(--ds-font-size-lg);
  font-weight: 600;
  color: var(--ds-gray-800);
}
.ds-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: var(--ds-spacing-md) var(--ds-spacing-lg);
  &__item { display: flex; flex-direction: column; gap: 4px; &--wide { grid-column: 1 / -1; } }
  &__label { font-size: var(--ds-font-size-sm); color: var(--ds-gray-500); }
  &__value { font-size: var(--ds-font-size-md); color: var(--ds-gray-800); word-break: break-all; }
}
.ds-file-link { margin-right: var(--ds-spacing-md); }
</style>
